// What components print (dynamic VAT spec §5 "Displays", customer service fee
// spec §1 "display rule"): numbers from stored line items, a preview or a
// snapshot, never from a hardcoded rate. Legacy transactions (no snapshot)
// keep today's 25.5 % everywhere. CommonJS: shared by server and client.
//
// No object spread/rest here: see the note in transaction.js stripCase —
// webpack 4 would treat this file as an ES module and the client bundle
// load would throw. Object.assign instead.
const { types } = require('./sdk');
const { resolveTransactionVat, PATH } = require('./transaction');
const { buildLineItems, lineTotal, LINE_ITEM, DEFAULT_FEE_PERCENTAGE } = require('./lineItems');
const { rateLabel } = require('./notes');

const { Money } = types;

const FEE_MODEL = { CUSTOMER: 'customer', PROVIDER: 'provider' };

const LEGACY_SUPPLY = {
  case: 'LEGACY',
  rate: 0.255,
  label: 'VAT',
  noteKey: 'STANDARD',
  placeOfSupply: 'FI',
};
const LEGACY_FEE = Object.assign({}, LEGACY_SUPPLY, { case: 'PF-1' });

// The Stripe resolution for an FI-registered seller and a Finnish consumer:
// what every transaction was until now, used to preview "today's" numbers.
const legacyPreviewResolution = () =>
  resolveTransactionVat({
    path: PATH.STRIPE,
    seller: {
      country: 'FI',
      postalCode: '00100',
      vatStatus: 'REGISTERED',
      vatNumber: 'FI00000000',
      homeVat: null,
      exemptId: null,
    },
    buyer: {
      country: 'FI',
      postalCode: '00100',
      customerType: 'CONSUMER',
      vatNumber: null,
      vatValidationStatus: 'NONE',
      businessProofRef: null,
    },
  });

// A snapshot is usable only in a shape vatFromTransaction accepts: v1
// (written while the fee was the provider's) or v2 (customer service fee).
const usableSnapshot = vat => !!(vat && (vat.v === 1 || vat.v === 2) && vat.supplies);

// Reads protectedData.vat and normalizes the per-path supply keys into a
// single customer/provider/serviceFee shape components can print without
// knowing which path a transaction took.
const vatFromTransaction = tx => {
  const vat = tx && tx.attributes && tx.attributes.protectedData && tx.attributes.protectedData.vat;
  if (!usableSnapshot(vat)) {
    return {
      legacy: true,
      path: PATH.STRIPE,
      feeModel: FEE_MODEL.PROVIDER,
      customer: LEGACY_SUPPLY,
      provider: LEGACY_SUPPLY,
      platformFee: LEGACY_FEE,
      serviceFee: null,
      customerPrice: null,
      supplies: null,
      seller: null,
      buyer: null,
    };
  }
  const s = vat.supplies;
  return {
    legacy: false,
    path: vat.path,
    feeModel: vat.v === 2 ? vat.feeModel || FEE_MODEL.CUSTOMER : FEE_MODEL.PROVIDER,
    customer: s.core || s.customerSale || null,
    provider: s.core || s.providerSale || null,
    platformFee: s.platformFee || null,
    serviceFee: s.serviceFee || null,
    customerPrice: typeof vat.customerPrice === 'number' ? vat.customerPrice : null,
    supplies: s,
    seller: vat.seller || null,
    buyer: vat.buyer || null,
  };
};

const resolutionFromSnapshot = vat => ({ path: vat.path, supplies: vat.supplies });

const okPreviewResolution = vatPreview =>
  (vatPreview && vatPreview.status === 'ok' && vatPreview.resolution) || null;

// The path a pending offer's accept will take for the selected payment
// method; mirrors the server's vat-preview `previewPath`.
const expectedPreviewPath = (processName, paymentMethod) => {
  if (paymentMethod === 'invoice') return PATH.INVOICE;
  if (processName === 'flex-light-process') return PATH.LIGHT_MAIN;
  return PATH.STRIPE;
};

// A preview fetched for another payment method prices and gates the wrong
// path, so it counts as no preview at all (R35).
const applicablePreview = (vatPreview, expectedPath) =>
  vatPreview && vatPreview.path === expectedPath ? vatPreview : null;

// The one rule every offer display follows for its RATES: the transaction's
// snapshot, else an ok server preview, else the legacy 25.5 % resolution.
// With `expectedPath`, the snapshot is a pending offer's own protectedData —
// forgeable by the provider via update-protected-data on make-custom-offer
// (R42) — so it is ignored entirely: an applicable ok preview wins, else the
// legacy resolution. Amounts of accepted transactions come from their line
// items (offerTotals), never from the snapshot.
const resolutionForOffer = (transaction, vatPreview, expectedPath) => {
  if (expectedPath) {
    const preview = applicablePreview(vatPreview, expectedPath);
    return okPreviewResolution(preview) || legacyPreviewResolution();
  }
  const view = vatFromTransaction(transaction);
  if (!view.legacy) {
    return resolutionFromSnapshot(transaction.attributes.protectedData.vat);
  }
  return okPreviewResolution(vatPreview) || legacyPreviewResolution();
};

const amountOf = li => (li.lineTotal ? li.lineTotal.amount : lineTotal(li).amount);
const forParty = party => li => (li.includeFor || []).includes(party);
const withCode = (code, party) => li => li.code === code && (!party || forParty(party)(li));
const sumLines = (items, predicate) =>
  items.filter(predicate).reduce((sum, li) => sum + amountOf(li), 0);

// Rates and labels per side from a resolution (or a snapshot's supplies).
const supplyLabels = resolution => {
  const supplies = (resolution && resolution.supplies) || {};
  const customerSupply = supplies.core || supplies.customerSale || null;
  const providerSupply = supplies.core || supplies.providerSale || null;
  const feeSupply = supplies.serviceFee || null;
  return {
    customerRate: customerSupply ? customerSupply.rate : 0,
    providerRate: providerSupply ? providerSupply.rate : 0,
    feeRate: feeSupply ? feeSupply.rate : 0,
    customerLabel: customerSupply ? customerSupply.label : 'VAT',
    providerLabel: providerSupply ? providerSupply.label : 'VAT',
    feeLabel: feeSupply ? feeSupply.label : 'VAT',
  };
};

const ZERO_TOTALS = {
  feeModel: FEE_MODEL.CUSTOMER,
  customerPrice: 0,
  net: 0,
  fee: 0,
  feeVat: 0,
  customerVat: 0,
  customerTotal: 0,
  providerNet: 0,
  providerVat: 0,
  payout: 0,
  feePercentage: DEFAULT_FEE_PERCENTAGE,
};

// The money fields of previewTotals from line items — ours (no lineTotal
// yet) or the API's — for either fee layout (spec §1 "totalsFromLineItems").
// Under the provider commission `fee` is what was deducted from the provider;
// under the service fee it is what the customer pays Freedomly.
const totalsFromLineItems = lineItems => {
  const items = (lineItems || []).filter(li => !li.reversal);
  const night = items.find(li => li.code === LINE_ITEM.NIGHT);
  const net = night ? amountOf(night) : 0;
  const customerFee = sumLines(items, withCode(LINE_ITEM.CUSTOMER_COMMISSION));
  const customerFeeVat = sumLines(items, withCode(LINE_ITEM.CUSTOMER_COMMISSION_VAT));
  const commission = -sumLines(items, withCode(LINE_ITEM.PROVIDER_COMMISSION)) || 0;
  const commissionVat = -sumLines(items, withCode(LINE_ITEM.PROVIDER_COMMISSION_VAT)) || 0;
  const customerVat = sumLines(items, withCode(LINE_ITEM.SALE_TAX, 'customer'));
  const customerTotal = sumLines(items, forParty('customer'));
  const payout = sumLines(items, forParty('provider'));
  const providerTaxLines =
    sumLines(items, withCode(LINE_ITEM.SALE_TAX, 'provider')) +
    sumLines(items, withCode(LINE_ITEM.PROVIDER_SALE_TAX, 'provider'));

  if (commission > 0 && customerFee === 0) {
    // Made under the provider commission. Transactions from before the
    // dynamic VAT feature took the commission on the VAT-inclusive gross
    // (its unit price is not the night line): their provider net and VAT are
    // the payout split by the sale-tax rate, as the receipts showed them.
    const commissionLine = items.find(withCode(LINE_ITEM.PROVIDER_COMMISSION));
    const providerSaleTax = items.find(withCode(LINE_ITEM.SALE_TAX, 'provider'));
    const grossBased =
      !!providerSaleTax && !!commissionLine.unitPrice && commissionLine.unitPrice.amount !== net;
    const commissionFeePercentage =
      Math.abs(Number(commissionLine.percentage)) || DEFAULT_FEE_PERCENTAGE;
    if (grossBased) {
      const pct = Number(providerSaleTax.percentage) || 25.5;
      const providerVat = Math.round((payout * pct) / (100 + pct));
      return {
        feeModel: FEE_MODEL.PROVIDER,
        customerPrice: net,
        net,
        fee: commission,
        feeVat: 0,
        customerVat,
        customerTotal,
        providerNet: payout - providerVat,
        providerVat,
        payout,
        feePercentage: commissionFeePercentage,
      };
    }
    return {
      feeModel: FEE_MODEL.PROVIDER,
      customerPrice: net,
      net,
      fee: commission,
      feeVat: commissionVat,
      customerVat,
      customerTotal,
      providerNet: net - commission,
      providerVat: providerTaxLines - commissionVat,
      payout,
      feePercentage: commissionFeePercentage,
    };
  }
  const feeLine = items.find(withCode(LINE_ITEM.CUSTOMER_COMMISSION));
  return {
    feeModel: FEE_MODEL.CUSTOMER,
    customerPrice: net + customerFee,
    net,
    fee: customerFee,
    feeVat: customerFeeVat,
    customerVat,
    customerTotal,
    providerNet: net,
    providerVat: providerTaxLines,
    payout,
    feePercentage: (feeLine && Number(feeLine.percentage)) || DEFAULT_FEE_PERCENTAGE,
  };
};

// Builds a preview from `buildLineItems` so previews equal what the server
// will store when the transaction is actually initiated.
const previewTotals = ({
  path,
  resolution,
  customerPrice,
  feePercentage = DEFAULT_FEE_PERCENTAGE,
}) => {
  const zero = Object.assign({}, ZERO_TOTALS, supplyLabels(resolution));
  if (
    !customerPrice ||
    customerPrice <= 0 ||
    !resolution ||
    resolution.blocked ||
    resolution.hold
  ) {
    return Object.assign(zero, { feePercentage });
  }
  const lineItems = buildLineItems({
    path,
    resolution,
    customerPrice: new Money(customerPrice, 'EUR'),
    feePercentage,
  });
  return Object.assign(zero, totalsFromLineItems(lineItems), { feePercentage });
};

// The one display rule (spec §1): a pending offer prints from the preview
// (any line items it carries are stale, left by a cancelled card payment);
// anything else with line items prints from them and is never recomputed
// from a snapshot. Transactions without line items (legacy pro process)
// keep the preview fallback.
const offerTotals = ({ lineItems, isPending, resolution, customerPrice, feePercentage }) => {
  const stored = (lineItems || []).filter(li => !li.reversal && li.lineTotal);
  if (isPending || stored.length === 0) {
    return previewTotals({ path: resolution.path, resolution, customerPrice, feePercentage });
  }
  return Object.assign({}, ZERO_TOTALS, supplyLabels(resolution), totalsFromLineItems(stored));
};

// VAT the customer paid, for analytics: on the work and on the fee.
const customerTaxTotal = lineItems =>
  sumLines(
    (lineItems || []).filter(li => !li.reversal && li.lineTotal),
    li =>
      (li.code === LINE_ITEM.SALE_TAX || li.code === LINE_ITEM.CUSTOMER_COMMISSION_VAT) &&
      forParty('customer')(li)
  );

// Provider-side split of a real transaction into net and VAT (spec §5,
// LineItemTotalPrice). A legacy transaction (no snapshot) never got VAT
// line items on the provider side, so it keeps today's maths byte-identical:
// vat = round(payoutTotal * 25.5 / 125.5), net = payoutTotal - vat. Every
// other transaction sums its actual line items.
const providerBreakdown = tx => {
  if (vatFromTransaction(tx).legacy) {
    const payoutTotal = tx && tx.attributes && tx.attributes.payoutTotal;
    if (!payoutTotal || typeof payoutTotal.amount !== 'number') {
      return { net: 0, vat: 0 };
    }
    const vat = Math.round((payoutTotal.amount * 25.5) / 125.5);
    return { net: payoutTotal.amount - vat, vat };
  }
  const lineItems = ((tx && tx.attributes && tx.attributes.lineItems) || []).filter(
    li => !li.reversal && li.includeFor && li.includeFor.includes('provider')
  );
  const vatCodes = [
    LINE_ITEM.SALE_TAX,
    LINE_ITEM.PROVIDER_SALE_TAX,
    LINE_ITEM.PROVIDER_COMMISSION_VAT,
  ];
  const sum = items => items.reduce((s, li) => s + (li.lineTotal ? li.lineTotal.amount : 0), 0);
  return {
    net: sum(lineItems.filter(li => !vatCodes.includes(li.code))),
    vat: sum(lineItems.filter(li => vatCodes.includes(li.code))),
  };
};

const rateLabelFor = (supply, locale) => (supply ? rateLabel(supply, locale) : '');

// Whether a display may say anything about VAT. A pending offer's rate comes
// from the server preview alone (the customer may not have given billing
// details yet, or VIES may still be answering), so until that preview is ok
// the display names the VAT-exclusive price and nothing else: no "+ VAT",
// no VAT-inclusive total. An accepted transaction prints its own line items,
// so its VAT is always known.
const offerVatKnown = (vatPreview, isPending) => !isPending || !!okPreviewResolution(vatPreview);

module.exports = {
  FEE_MODEL,
  legacyPreviewResolution,
  vatFromTransaction,
  resolutionFromSnapshot,
  resolutionForOffer,
  expectedPreviewPath,
  applicablePreview,
  previewTotals,
  totalsFromLineItems,
  offerTotals,
  customerTaxTotal,
  providerBreakdown,
  rateLabelFor,
  offerVatKnown,
};
